import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  GsFormControl,
  GsFormController,
  GsFormField,
  GsFormFieldAlert,
  GsFormFieldBasic,
  GsFormFieldCheckbox,
  GsFormFieldColor,
  GsFormFieldDatepicker,
  GsFormFieldFile,
  GsFormFieldForm,
  GsFormFieldLabel,
  GsFormFieldNumber,
  GsFormFieldObjectSelectAsync,
  GsFormFieldOption,
  GsFormFieldSelect,
  GsFormFieldText,
  GsFormFieldTextarea,
  GsFormFieldTextType,
  GsFormIf,
  GsFormIfArg,
  GsFormLabeled,
  GsFormWithFields,
  GsFormWithIf
} from 'libs/shared/src/components';
import {
  IFolder,
  IObjectSelectCreateInfo,
  IObjectSelectData,
  ISelect,
  ISelectAsyncResponse,
  ObjectSelectLoadData
} from 'libs/shared/src/components/gs-object-select/interfaces/object-select';
import {
  IInputIf,
  IInputIfArgument,
  IInputIfChaining,
  IInputRender,
  InputBaseValueTypes,
  InputType,
  IPaginated,
  mapDotNetValueType,
  TextBoxInputType
} from 'libs/shared/src/models';
import { DataProvider } from 'libs/shared/src/providers';
import { ActionsService } from 'libs/shared/src/services/actions/actions.service';
import { encodeBase64Unicode } from 'libs/shared/src/services/encoding/encoding.service';
import { SystemService } from 'libs/shared/src/services/system.service';
import { firstValueFrom } from 'rxjs';

export const TEXT_INPUT_TYPE_MAP: Record<TextBoxInputType, GsFormFieldTextType | undefined> = {
  [TextBoxInputType.Color]: undefined,
  [TextBoxInputType.Date]: undefined,
  [TextBoxInputType.Datetime]: undefined,
  [TextBoxInputType.Datetime_local]: undefined,
  [TextBoxInputType.Email]: 'email',
  [TextBoxInputType.Fax]: undefined,
  [TextBoxInputType.Html]: undefined,
  [TextBoxInputType.Markup]: undefined,
  [TextBoxInputType.Month]: undefined,
  [TextBoxInputType.Number]: undefined,
  [TextBoxInputType.Password]: 'password',
  [TextBoxInputType.Search]: undefined,
  [TextBoxInputType.Tel]: undefined,
  [TextBoxInputType.Text]: 'text',
  [TextBoxInputType.Time]: undefined,
  [TextBoxInputType.Url]: 'url',
  [TextBoxInputType.Week]: undefined,
  [TextBoxInputType.TextOrEmail]: undefined
};

@Injectable({
  providedIn: 'root'
})
export class FormsMapService {
  // Services
  private readonly translateService = inject(TranslateService);
  private readonly systemService = inject(SystemService);
  private readonly actionsService = inject(ActionsService);

  // Methods
  /**
   * Maps a backend input render to a frontend form field.
   * @param field - The backend input render to map.
   * @returns The frontend form field.
   */
  mapToController<T>(field: IInputRender<T>): GsFormController<T> {
    return {
      formControlName: DataProvider.mapCasesReverse(field.name),
      default: field.value,
      nullable: field.nullable,
      nullPlaceholder: field.placeHolder,
      constraints: field.constraints?.length ? field.constraints : undefined,
      readonly: field.readonly || undefined
    } as GsFormController<T>;
  }

  /**
   * Maps a backend input render to a frontend form control.
   * @param field - The backend input render to map.
   * @returns The frontend form control.
   */
  mapToControl(field: IInputRender): GsFormControl<InputBaseValueTypes> {
    return {
      ...this.mapToController(field),
      type: 'control'
    } as GsFormControl<InputBaseValueTypes>;
  }

  /**
   * Maps a backend input render to a frontend form field with if.
   * @param field - The backend input render to map.
   * @returns The frontend form field with if.
   */
  mapWithIf<T>(field: IInputRender<T>): GsFormWithIf {
    return {
      if: field.isVisible ? this.mapIf(field.isVisible) : undefined
    };
  }

  /**
   * Maps a backend input render to a frontend form with fields.
   * @returns The frontend form with fields.
   */
  mapWithFields(): GsFormWithFields {
    return {
      fields: []
    };
  }

  /**
   * Maps a backend input render to a frontend form labeled.
   * @param field - The backend input render to map.
   * @returns The frontend form labeled.
   */
  mapLabeled<T>(field: IInputRender<T>): GsFormLabeled {
    return {
      label: field.text,
      description: field.hint,
      labelPosition: 'top'
    };
  }

  /**
   * Maps a backend input render to a frontend form field basic.
   * @param field - The backend input render to map.
   * @returns The frontend form field basic.
   */
  mapBasic<T>(field: IInputRender<T>): GsFormFieldBasic<T> {
    return {
      ...this.mapToController<T>(field),
      ...this.mapWithIf(field),
      ...this.mapLabeled(field),
      groupingName: field.group || undefined,
      tabName: field.tabSheet?.name || undefined
    } as GsFormFieldBasic<T>;
  }

  /**
   * Maps a backend input render to a frontend form field select.
   * @param field - The backend input render to map.
   * @returns The frontend form field select.
   */
  mapSelect(field: IInputRender): GsFormFieldSelect<InputBaseValueTypes> {
    // A multi-select combo box (e.g. `AddEnums` "Anreden Optionen") transports both its options
    // and its selection as strings, the selection as a comma-separated list ("Mr,Mrs"), and has
    // no null option: an empty selection is the empty string.
    const multiple = field.multiSelect === true;
    const valueType = multiple ? 'string' : mapDotNetValueType(field.valueType);
    const nullOption = field.values.find((val) => val.value == null);
    let options: GsFormFieldOption<InputBaseValueTypes>[] = field.values
      .filter((val) => val.value != null)
      .map((val) => ({
        text: val.text,
        value: this.coerceValueType(val.value as InputBaseValueTypes, valueType)
      }));
    const hasNullOption = !multiple && (field.nullable || field.value == null);
    if (hasNullOption) {
      const nullText = nullOption?.text || this.translateService.instant('gsForm.select.placeholder');
      options = [{ text: nullText, value: null }, ...options];
    }
    return {
      ...this.mapBasic(field),
      type: 'select',
      options: options,
      valueType: valueType,
      ...(multiple ? { multiple: true } : {}),
      ...(hasNullOption && field.value == null ? { default: null } : {})
    } as GsFormFieldSelect<InputBaseValueTypes>;
  }

  /**
   * Coerces a raw option value to the select field's actual value type.
   * The backend serializes all combo-box option values as strings (e.g. a
   * boolean field yields "True"/"False"), while the current value and
   * `valueType` are typed. Without this coercion the typed control value never
   * matches the string option values, so the dropdown shows no selection.
   * @param value - The raw option value coming from the backend.
   * @param valueType - The target value type of the select field.
   * @returns The value coerced to the given type (null stays null).
   */
  private coerceValueType(value: InputBaseValueTypes | null, valueType: 'string' | 'number' | 'boolean'): InputBaseValueTypes | null {
    if (value == null) return null;
    switch (valueType) {
      case 'number':
        return Number(value);
      case 'boolean':
        return typeof value === 'boolean' ? value : String(value).trim().toLowerCase() === 'true';
      default:
        return String(value);
    }
  }

  /**
   * Maps a backend TextBox input render to the correct frontend form field type.
   * @param field - The backend input render to map.
   * @returns The frontend form field (text, number, datepicker, or color).
   */
  mapTextBox(field: IInputRender): GsFormField<InputBaseValueTypes> {
    if (field.multiLine) {
      return this.mapTextarea(field);
    }
    switch (field.textBoxInputType) {
      case TextBoxInputType.Number:
        return this.mapNumber(field);
      case TextBoxInputType.Color:
        return this.mapColor(field);
      case TextBoxInputType.Date:
      case TextBoxInputType.Datetime:
      case TextBoxInputType.Datetime_local:
      case TextBoxInputType.Month:
      case TextBoxInputType.Time:
      case TextBoxInputType.Week:
        return this.mapDatepicker(field);
      default:
        return this.mapText(field);
    }
  }

  /**
   * Maps a backend input render to a frontend form field text.
   * @param field - The backend input render to map.
   * @returns The frontend form field text.
   */
  mapText(field: IInputRender): GsFormFieldText {
    return {
      ...this.mapBasic(field),
      type: 'text',
      inputType: TEXT_INPUT_TYPE_MAP[field.textBoxInputType] ?? 'text',
      placeholder: field.placeHolder
    } as GsFormFieldText;
  }

  /**
   * Maps a backend FileUpload input render to a frontend form field file.
   * The selected file is stored as a Base64 data URL by the gs-file input-type.
   * @param field - The backend input render to map.
   * @returns The frontend form field file.
   */
  mapFile(field: IInputRender): GsFormFieldFile {
    return {
      ...this.mapBasic(field),
      type: 'file',
      accept: field.accept
    } as GsFormFieldFile;
  }

  /**
   * Maps a backend multiLine input render to a frontend form field textarea.
   * @param field - The backend input render to map.
   * @returns The frontend form field textarea.
   */
  mapTextarea(field: IInputRender): GsFormFieldTextarea {
    return {
      ...this.mapBasic(field),
      type: 'textarea',
      placeholder: field.placeHolder,
      rows: field.rows || undefined
    } as GsFormFieldTextarea;
  }

  /**
   * Maps a backend input render to a frontend form field number.
   * @param field - The backend input render to map.
   * @returns The frontend form field number.
   */
  mapNumber(field: IInputRender): GsFormFieldNumber {
    return {
      ...this.mapBasic(field),
      type: 'number',
      placeholder: field.placeHolder
    } as GsFormFieldNumber;
  }

  /**
   * Maps a backend input render to a frontend form field datepicker.
   * @param field - The backend input render to map.
   * @returns The frontend form field datepicker.
   */
  mapDatepicker(field: IInputRender): GsFormFieldDatepicker {
    return {
      ...this.mapBasic(field),
      type: 'datepicker'
    } as GsFormFieldDatepicker;
  }

  /**
   * Maps a backend input render to a frontend form field color.
   * @param field - The backend input render to map.
   * @returns The frontend form field color.
   */
  mapColor(field: IInputRender): GsFormFieldColor {
    return {
      ...this.mapBasic(field),
      type: 'color',
      customOnly: true
    } as GsFormFieldColor;
  }

  /**
   * Maps a backend input render to a frontend form field checkbox.
   * @param field - The backend input render to map.
   * @returns The frontend form field checkbox.
   */
  mapCheckbox(field: IInputRender): GsFormFieldCheckbox {
    return {
      ...this.mapBasic(field),
      type: 'checkbox'
    } as GsFormFieldCheckbox;
  }

  /**
   * Maps a backend input render (Label or Headline) to a frontend form field label.
   * @param field - The backend input render to map.
   * @returns The frontend form field label.
   */
  mapLabel(field: IInputRender): GsFormFieldLabel {
    return {
      ...this.mapBasic(field),
      type: 'label',
      nullable: true,
      label: typeof field.value === 'string' && field.value.length > 0 ? field.value : field.text
    } as GsFormFieldLabel;
  }

  /**
   * Maps a Custom input to the correct schema type based on customType.
   */
  private mapCustom(field: IInputRender): GsFormField<InputBaseValueTypes> {
    switch (field.customType) {
      case 'Alert':
        return this.mapAlert(field);
      case 'FormBuilder':
        if (!this.systemService.isDevelopment) return {} as GsFormField<InputBaseValueTypes>;
        return this.mapFormBuilder(field);
      default:
        console.warn(`[FormsMapService] Unknown custom type: ${field.customType}`);
        return {} as GsFormField<InputBaseValueTypes>;
    }
  }

  /**
   * Maps a Custom Alert input to a GsFormFieldAlert.
   */
  private mapAlert(field: IInputRender): GsFormFieldAlert {
    return {
      ...this.mapBasic(field),
      type: 'alert',
      nullable: true,
      label: field.text
    } as GsFormFieldAlert;
  }

  /**
   * Maps a Custom FormBuilder input to a GsFormFieldForm.
   */
  private mapFormBuilder(field: IInputRender): GsFormFieldForm {
    return {
      ...this.mapBasic(field),
      type: 'form',
      nullable: true,
      label: field.customTitle || field.text || 'editForm',
      modalTitle: field.customTitle || field.text || 'editForm'
    } as GsFormFieldForm;
  }

  /**
   * Maps a backend input render to a frontend form field.
   * @param input - The backend input render to map.
   * @returns The frontend form field.
   * @description Some input-types are not mapable and skipped (please ask the team in case of new supported types)
   */
  mapInputToField(input: IInputRender): GsFormField<InputBaseValueTypes> {
    // ponytail: backend file-upload inputs arrive as TextBox + customType "FileUpload" (legacy CS3 shape),
    // so the inputType switch would render them as text — map on customType before it runs.
    if (input.customType === 'FileUpload') return this.mapFile(input);

    // Warn about layout properties that are not supported in the schema system
    if (input.row) {
      console.warn(`[FormsMapService] Row layout is not supported in schema — field "${input.name}" has row "${input.row}"`);
    }
    if (
      input.width &&
      !input.width.isEmpty &&
      (input.width.valueLG !== 12 ||
        input.width.valueMD !== 12 ||
        input.width.valueSM !== 12 ||
        input.width.valueXS !== 12 ||
        input.width.valueXXS !== 12)
    ) {
      console.warn(`[FormsMapService] Responsive width is not supported in schema — field "${input.name}"`, input.width);
    }

    switch (input.inputType) {
      case InputType.TextBox:
        return this.mapTextBox(input);
      case InputType.CheckBox:
        return this.mapCheckbox(input);
      case InputType.ComboBox:
      case InputType.RadioGroup:
        return this.mapSelect(input);
      case InputType.DropDown:
        return this.mapDropDown(input);
      case InputType.Label:
      case InputType.Headline:
        return this.mapLabel(input);
      case InputType.Hidden:
        return this.mapToControl(input);
      case InputType.Custom:
        return this.mapCustom(input);
      case InputType.FileUpload:
        return this.mapFile(input);
      case InputType.Separator:
        console.warn(
          `[FormsMapService] Separator (InputType ${input.inputType}) is not supported in schema — use groups for visual separation`
        );
        return {} as GsFormField<InputBaseValueTypes>;
      case InputType.HtmlBox:
        console.warn(
          `[FormsMapService] HtmlBox (InputType ${input.inputType}) is not supported in schema — raw HTML rendering is not allowed`
        );
        return {} as GsFormField<InputBaseValueTypes>;
    }
    console.warn(`[FormsMapService] Unsupported input type: ${input.inputType}`, input);
    return {} as GsFormField<InputBaseValueTypes>;
  }

  /**
   * Maps a backend DropDown input to a GsFormFieldObjectSelectAsync.
   * Builds an async loadData closure using ActionsService for pagination, folders, and search.
   * Does NOT set objectItemTemplate — the designer overrides this via GS_FORM_CUSTOM_FIELDS.
   * @param field - The backend input render to map.
   * @returns The frontend form field object-select-async.
   */
  mapDropDown(field: IInputRender): GsFormFieldObjectSelectAsync {
    const action = field.select?.dialog?.action;
    const createInfo = signal<IObjectSelectCreateInfo | undefined>(undefined);
    const loadData: ObjectSelectLoadData<unknown> = async (
      page?: number,
      folderId?: number,
      search?: string,
      selectedValues?: string[]
    ) => {
      if (!action) throw new Error('No action defined for dropdown');
      const urlParams: Record<string, unknown> = { pageIndex: page };
      if (folderId != null && folderId >= 0) urlParams['parentID'] = folderId;
      if (search && search.trim().length > 0) {
        urlParams['search'] = encodeBase64Unicode(search.trim());
      }
      // Endpoints that support it list the current selection first; the rest ignore the parameter.
      if (selectedValues?.length) urlParams['selectedIDs'] = selectedValues.join(',');
      const response = await firstValueFrom(
        this.actionsService.callAction<IPaginated<ISelect<unknown>> | ISelectAsyncResponse<unknown>>(
          action,
          urlParams as Record<string, any>
        )
      );
      if (!response) return { objects: [] };
      // Upload-capable endpoints wrap the list under `items` and advertise a create method + dialog;
      // plain endpoints return the paginated list directly. Clear createInfo when it is absent so a
      // stale upload button does not linger after switching to an endpoint without upload support.
      const wrapped = 'items' in response ? response : undefined;
      if (wrapped?.methods?.create && wrapped.dialog) {
        createInfo.set({ method: wrapped.methods.create, dialog: wrapped.dialog });
      } else {
        createInfo.set(undefined);
      }
      const paginated = wrapped ? wrapped.items : (response as IPaginated<ISelect<unknown>>);
      if (!paginated) return { objects: [] };
      const { data: items, ...paginate } = paginated;
      const { folders, objects } = this.separateFoldersAndObjects(items);
      return { folders, objects, paginate } as IObjectSelectData<ISelect<unknown>>;
    };

    return {
      ...this.mapBasic(field),
      type: 'object-select-async',
      loadData,
      createInfo,
      multiple: field.multiSelect ?? false,
      sortable: field.sortable ?? false,
      headerTitle: field.select?.dialog?.title,
      headerSubTitle: field.select?.dialog?.subTitle,
      placeholderTitle: field.placeHolder ?? (field.select?.dialog?.title ? field.select.dialog.title + '...' : undefined),
      doneButtonText: field.select?.dialog?.okButtonText,
      dialogName: field.select?.dialog?.name
    } as GsFormFieldObjectSelectAsync;
  }

  /**
   * Maps a backend if to a frontend schema if.
   * @param backendIf - The backend if to map.
   * @returns The frontend schema if.
   */
  mapIf(backendIf: IInputIf): GsFormIf {
    return {
      args: backendIf.args.map((arg) => this.mapIfArg(arg)),
      chaining: backendIf.chaining == IInputIfChaining.AND ? 'and' : 'or'
    };
  }

  /**
   * Maps a backend if argument to a frontend schema if argument.
   * @param backendIfArg - The backend if argument to map.
   * @returns The frontend schema if argument.
   */
  mapIfArg(backendIfArg: IInputIfArgument): GsFormIfArg {
    return {
      formControlName: DataProvider.mapCasesReverse(backendIfArg.name),
      operator: backendIfArg.operator,
      value: backendIfArg.value
    };
  }

  /**
   * Separates a mixed list of selectable items into folders and non-folder objects in a single pass.
   * @param items - The list of selectable items from the backend.
   * @returns An object with folders (mapped to IFolder) and objects (non-folder items).
   */
  private separateFoldersAndObjects(items: ISelect<unknown>[]): { folders: IFolder[]; objects: ISelect<unknown>[] } {
    const folders: IFolder[] = [];
    const objects: ISelect<unknown>[] = [];
    for (const item of items ?? []) {
      if (this.isFolder(item.model)) {
        const o = item as ISelect<IFolder>;
        folders.push({ iD: o.model!.iD, name: o.model!.name, hasChildren: o.model!.hasChildren, path: o.model!.path });
      } else {
        objects.push(item);
      }
    }
    return { folders, objects };
  }

  /**
   * Checks if the given object is a folder.
   */
  private isFolder(obj: unknown): obj is IFolder {
    return (
      !!obj &&
      typeof obj === 'object' &&
      'isFolder' in obj &&
      'name' in obj &&
      'hasChildren' in obj &&
      !!(obj as Record<string, unknown>)['isFolder']
    );
  }
}
